import { Component } from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import lodash from 'lodash';

import PageParent from 'components/common/PageParent/PageParent';
import { ComposablePageHeader, MainContent, SideContent } from 'components/common/PageHeader/PageHeader';
import { Button, BUTTON } from 'components/common/Button/Button';
const { COLOR, BGCOLOR } = BUTTON;
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import ListHelperText from 'components/common/Helper/ListHelper';
import DateTime from 'common/components/DateTime/DateTime';

import FilePicker from 'components/common/File/FilePicker';

import { binderInstitutionIdSelector } from 'common/selectors';
import { loadMimeTypes } from 'common/actions/fileUploadActions';

import { 
    retrieveBinderValidation,
    unloadBinderValidation,
    writeUploadedDocument,
    setValidationRequirement,
    syncBinderValidationStaffMembers,
    checkBinderValidationStaffMembers,
    importBinderValidationTemplate,
} from 'pages/binder/actions/binderValidation';

import {
    binderValidationSelector,
    binderValidationLoadingSelector,
} from 'pages/binder/selectors';

import ExpandedValidation from './ExpandedValidation';
import styles from './BinderValidation.scss';

class BinderValidation extends Component {

    constructor() {
        super();
        this.state = {
            activeTabIndex: 0,
            activeDocumentValidationId: null,
            expandValidation: false,
            revalidationRequired: false,
            conversionMessageQueueCount: 0
        };
    }

    componentDidMount() {
        const { binderId, retrieveBinderValidation, setSidebarCollapsed, loadMimeTypes } = this.props;
        const skipLoadingStatus = false;
        loadMimeTypes();
        setSidebarCollapsed(true);
        retrieveBinderValidation(binderId, skipLoadingStatus);
    }

    componentWillUnmount() {
        const { setSidebarCollapsed, unloadBinderValidation } = this.props;
        setSidebarCollapsed(false);
        unloadBinderValidation();
    }

    componentDidUpdate(prevProps, prevState) {
        const { activeDocumentValidationId, conversionMessageQueueCount } = this.state;
        const { writeUploadedDocument, lastUploadedDocument, allDocumentValidations } = this.props;
        const lastUploadedDocumentHasChanged = !lodash.isEqual(this.props.lastUploadedDocument, prevProps.lastUploadedDocument);
        const binderValidationHasLoaded = !prevProps.allDocumentValidations && allDocumentValidations;

        if(binderValidationHasLoaded) {
            this.props.checkBinderValidationStaffMembers(this.props.binderId);
        }

        // Writes the last uploaded document to validation state keyed by documentTypeId for easy retrieval
        if(!lodash.isEmpty(lastUploadedDocument) && lastUploadedDocumentHasChanged && conversionMessageQueueCount > 0) {
            const activeDocumentValidation = lodash.get(allDocumentValidations, `${activeDocumentValidationId}`);
            writeUploadedDocument(lastUploadedDocument, activeDocumentValidation.id);
            this.setState({
                conversionMessageQueueCount: conversionMessageQueueCount - 1
            });
        }
    }

    validationOnClickHandler(documentValidation) {
        const documentValidationId = documentValidation.id;
        this.setState({
            expandValidation: true,
            activeDocumentValidationId: documentValidationId,
        });
    
    }

    validationDocumentOnClick(document) {
        const { binderId } = this.props;
        const newTabPath = `/binder/${binderId}/files/${document.id}`;
        window.open(newTabPath, `Viewing: ${document.name}`);
    }

    onTabClickHandler(index) {
        this.setState({activeTabIndex: index});
    }

    setRevalidationRequired(isRequired) {
        this.setState({revalidationRequired: isRequired});
    }

    setDocumentValidationRequirement(isRequired) {
        const { activeDocumentValidationId } = this.state;
        this.props.setValidationRequirement(activeDocumentValidationId, isRequired);
    }

    onfile(document, type, documentTypeId) {
        const { binderId } = this.props;
        const path = `/binder/${binderId}/files/${document.id}/${type}?rd=none`;
        const newTabPath = documentTypeId ? path + `&dt=${documentTypeId}`: path;
        window.open(newTabPath, '_blank');
        this.setRevalidationRequired(true);
    }

    onReValidation() {
        const { binderId, retrieveBinderValidation } = this.props;
        const skipLoadingStatus = true;
        this.setRevalidationRequired(false);
        retrieveBinderValidation(binderId, skipLoadingStatus);
    }

    openFilepicker() {
        this.refs.filePicker.openFilePicker();
        // There are 2 conversion messages for each file upload
        // 1 at the start of conversion and 1 at its conclusion.
        this.setState({
            conversionMessageQueueCount: this.state.conversionMessageQueueCount + 2
        });
    }

    onBinderValidationSync(staffMembersAdded, staffMembersReAdded, staffMembersRemoved) {
        const { binderId, binderTemplateId } = this.props;
        this.props.syncBinderValidationStaffMembers(
            binderId, 
            binderTemplateId, 
            staffMembersAdded, 
            staffMembersReAdded, 
            staffMembersRemoved);
    }

    onBinderValidationTemplateImport() {
        const { binderId, binderTemplateId } = this.props;
        this.props.importBinderValidationTemplate(binderId, binderTemplateId);
    }

    getStudyDocumentsValidation() {
        const { studyDocument } = this.props;
        const studyDocumentValidations = lodash.get(studyDocument, 'validations', []);
        if(!lodash.isEmpty(studyDocumentValidations)) {
            const studyDocumentsGrid = studyDocumentValidations.map((dv, index) => {
                if(!dv.isRequired) {
                    return <ValidationNotRequired key={index} gridClass={styles.studyGridBlock} documentValidation={dv} onClickHandler={() => this.validationOnClickHandler(dv)} />;
                }
                const validationStatus = getValidationStatus(dv);
                const documentTypeClass = dv.isValid ? styles.documentTypeAvailable : styles.documentTypeUnavailable;
                return (
                    <ValidationGridBlock key={index} gridClass={styles.studyGridBlock} isValid={dv.isValid} onClickHandler={() => this.validationOnClickHandler(dv)} data-cy="BinderValidation">
                        <span className={`${styles.ValidationGridBlockIndicator} ${validationStatus.style}`}>
                            <span className={styles.ValidationGridBlockMessage} data-cy="BinderValidation-message">{validationStatus.message}</span>
                            <span className={styles.ValidationGridBlockDocumentCount} data-cy="BinderValidation-actualNumOfCopies">{dv.actualNumOfCopies}</span>
                        </span>
                        <div className={styles.studyGridBlockBody}>
                            <div className={documentTypeClass} data-cy="BinderValidation-documentTypeName">
                                {dv.documentTypeName}
                            </div>
                            <div data-cy="BinderValidation-label">
                                {validationStatus.label}
                            </div>
                        </div>
                    </ValidationGridBlock>
                );
            });
            return (
                <div className={styles.ValidationGrid} data-cy="BinderValidation-studyDocumentsGrid">
                    {studyDocumentsGrid}
                </div>
            );
        }
    }

    getStaffDocumentsValidation() {
        const { staffDocument } = this.props;
        const staffDocumentValidations = lodash.get(staffDocument, 'validations', []);
        if(!lodash.isEmpty(staffDocumentValidations)) {
            return <ValidationGrid 
                documentValidations={staffDocument.validations} 
                onClickHandler={this.validationOnClickHandler.bind(this)} />;
        }
    }

    getTabLabels() {
        const { studyDocument, staffDocument } = this.props;
        const tabLabels = [];

        if(!lodash.isEmpty(studyDocument.validations)) {
            tabLabels.push(`Study Documents - ${studyDocument.percentComplete}%`);
        }

        if(!lodash.isEmpty(staffDocument.validations)) {
            tabLabels.push(`Staff Documents - ${staffDocument.percentComplete}%`);
        }

        return tabLabels;
    }

    getRevalidationOptions() {
        const { revalidationRequired } = this.state;
        return (
            <div className={styles.revalidationOptions}>
                {revalidationRequired && <span className={styles.helperText}>Re-Validation may be required</span>}
                <Button
                    text={'Re-Validate'}
                    disabled={!revalidationRequired}
                    color={COLOR.WHITE}
                    bgcolor={BGCOLOR.BLUE}
                    onClick={this.onReValidation.bind(this)} 
                    data-cy="BinderValidation-onReValidation"/>
            </div>
        );
    }

    getSyncBinderValidationSection() {
        const { staffMemberChanges } = this.props;
        const { syncRequired, staffMembersAdded=[], staffMembersReAdded=[], staffMembersRemoved=[] } = staffMemberChanges;
        const staffAdded = {...staffMembersAdded, ...staffMembersReAdded};
        if(syncRequired) {
            return (
                <div className={styles.SyncBinderValidationSection} data-cy="BinderValidation-return">
                    <div className={styles.staffSection}>
                        {!lodash.isEmpty(staffAdded) && 
                            <span className={styles.staffMembersAdded} data-cy="BinderValidation-Added">Staff Members Added: 
                                {lodash.map(staffAdded, staff => <p className={styles.staffName}>{staff.name} </p>)}
                            </span>
                        }
                        {!lodash.isEmpty(staffMembersRemoved) && 
                            <span className={styles.staffMembersRemoved} data-cy="BinderValidation-Removed">Staff Members Removed: 
                                {lodash.map(staffMembersRemoved, staff => <p className={styles.staffName}>{staff.name} </p>)}
                            </span>
                        }
                    </div>
                    <div className={styles.actionSection}>
                        <Button
                            text={'Apply Changes'}
                            disabled={!syncRequired}
                            color={COLOR.BLUE}
                            onClick={() => this.onBinderValidationSync(staffMembersAdded, staffMembersReAdded, staffMembersRemoved)} 
                            data-cy="BinderValidation-Apply"/>
                    </div>
                </div>
            );
        }
    }

    render() {
        const { 
            isLoading, 
            studyDocument, 
            staffDocument, 
            allDocumentValidations, 
            binderId, 
            institutionId,
            allowedMimeTypes, 
            fileExtensionLookup, 
            store, 
            uploads 
        } = this.props;

        const { activeDocumentValidationId, activeTabIndex, expandValidation } = this.state;
        const activeDocumentValidation = lodash.get(allDocumentValidations, `${activeDocumentValidationId}`);

        if(isLoading) {
            return <LoadingStatus itemName='Binder Validation' loading />;
        }

        if(!studyDocument && !staffDocument) {
            return (
                <div className={styles.emptyValidation} data-cy="BinderValidation-render">
                    <ListHelperText 
                        primaryText={'Binder validation has not been configured yet.'} 
                        secondaryText={'To configure, import your standard template.'} />
                    <Button 
                        text={'Import Template'}
                        color={COLOR.WHITE}
                        bgcolor={BGCOLOR.ORANGE}
                        onClick={() => this.onBinderValidationTemplateImport()}
                        data-cy="BinderValidation-Import"/>
                </div>);
        }

        return (
            <div className={styles.ValidationContainer} data-cy="BinderValidation-div">
                <PageParent>
                    <ComposablePageHeader>
                        <MainContent>
                            <div className={styles.pageHeaderTitle}>Validation</div>
                        </MainContent>
                        <SideContent>
                            {this.getRevalidationOptions()}
                        </SideContent>
                    </ComposablePageHeader>
                    {this.getSyncBinderValidationSection()}
                    <TabViewer labels={this.getTabLabels()} activeTabIndex={activeTabIndex} onTabClick={this.onTabClickHandler.bind(this)}>
                        {this.getStudyDocumentsValidation()}
                        {this.getStaffDocumentsValidation()}
                    </TabViewer>
                    {expandValidation && 
                        <ExpandedValidation 
                            onDocumentClick={this.validationDocumentOnClick.bind(this)}
                            onClose={()=> this.setState({expandValidation:false, activeDocumentValidationId: null})}
                            onfile={this.onfile.bind(this)}
                            uploadedDocuments={uploads}
                            openFilePicker={this.openFilepicker.bind(this)}
                            setDocumentValidationRequirement={this.setDocumentValidationRequirement.bind(this)}
                            documentValidation={activeDocumentValidation}
                            isExpanded={expandValidation} />
                    }
                    <div className={styles.ValidationFooter}></div>
                    <FilePicker
                        ref="filePicker"
                        location={location}
                        multiple={false}
                        binderId={binderId}
                        institutionId = {institutionId}
                        allowedMimeTypes={allowedMimeTypes}
                        fileExtensionLookup={fileExtensionLookup}
                        store={store} />
                </PageParent>
            </div>
        );
    }

}

const ValidationGridBlock = ({gridClass, key, onClickHandler, children}) => {
    const hoverClass = styles.validGridBlock;
    return (
        <span key={key} className={`${gridClass} ${hoverClass}`} onClick={onClickHandler} data-cy={'BinderValidation-' + key}>
            {children}
        </span>
    );
};

const ValidationNotRequired = ({key, gridClass, documentValidation, onClickHandler, isRemoved}) => {
    const removedClass = isRemoved ? styles.RemovedGridBlock: '';
    return (
        <ValidationGridBlock key={key} gridClass={`${gridClass} ${removedClass}`} onClickHandler={onClickHandler} data-cy={key + '-BinderValidation'}>
            <span className={`${styles.ValidationGridBlockIndicator} ${styles.ValidationNotRequired}`}>
                <span className={`${styles.ValidationGridBlockMessage} ${styles.ValidationNotRequiredText}`}>NOT REQUIRED</span>
            </span>
            <div className={styles.staffGridBlockBody}>
                <div className={styles.ValidationNotRequiredText} data-cy="BinderValidation-documentTypeNames">
                    {documentValidation.documentTypeName}
                </div>
            </div>
        </ValidationGridBlock>
    );
};

const ValidationGrid = ({documentValidations, onClickHandler}) => {
    const gridRows = lodash.map(documentValidations, (dv, key) => {
        let removedCount = 0, removalDate;
        const dvs = lodash.sortBy(dv, ['documentTypeName']).map((d, index) => {
            if(d.removalDate) {
                removedCount += 1;
                removalDate = d.removalDate;
            }
            if(!d.isRequired || d.removalDate) {
                return <ValidationNotRequired key={index} gridClass={styles.staffGridBlock} isRemoved={!lodash.isNil(d.removalDate)} documentValidation={d} onClickHandler={() => onClickHandler(d)} />;
            }
            const validationStatus = getValidationStatus(d);
            const documentTypeClass = d.isValid ? styles.documentTypeAvailable : styles.documentTypeUnavailable;
            return (
                <ValidationGridBlock key={index} gridClass={styles.staffGridBlock} validationStatus={validationStatus} onClickHandler={() => onClickHandler(d)} data-cy={'BinderValidation-grid' + key}>
                    <span className={`${styles.ValidationGridBlockIndicator} ${validationStatus.style}`}>
                        <span className={styles.ValidationGridBlockMessage}>{validationStatus.message}</span>
                        <span className={styles.ValidationGridBlockDocumentCount}>{d.actualNumOfCopies}</span>
                    </span>
                    <div className={styles.staffGridBlockBody}>
                        <div className={documentTypeClass} data-cy="BinderValidation-Name">
                            {d.documentTypeName}
                        </div>
                        <div data-cy="BinderValidation-labels">
                            {validationStatus.label}
                        </div>
                    </div>
                </ValidationGridBlock>
            );
        });

        const userHasBeenRemoved = removedCount === dv.length;
        return (
            <div key={key} className={styles.ValidationGridRow} data-cy={'BinderValidationRemoved-' + key}>
                <span className={styles.StudyValidationSection}>
                    <span>
                        <span className={styles.StudyValidationName} data-cy={'BinderValidationKey-' + key}>{key}</span>
                        {userHasBeenRemoved && <span  className={styles.StudyValidationRemoved}>Removed 
                            <DateTime value={removalDate} format={'MMM DD, YYYY'} data-cy={'BinderValidationDate-' + key}/>
                        </span>}
                        <span className={styles.StudyValidationSettings}>Settings</span>
                    </span>
                </span>
                {dvs}
            </div>
        );
    });

    return(
        <div className={styles.ValidationGrid} data-cy="BinderValidation-gridRows">
            {gridRows}
        </div>
    );
};

const TabViewer = ({labels, children, activeTabIndex, onTabClick}) => {

    const activeChild = lodash.get(children, `[${activeTabIndex}]`);
    const tabClass = index => index === activeTabIndex ? styles.tab_active : styles.tab;

    const tabs = labels.map((label, index) => <span data-cy={'BinderValidationBtn-' + index} key={index} className={tabClass(index)} onClick={() => onTabClick(index)} >{label}</span>);

    return (
        <div className={styles.tabViewer} data-cy="BinderValidation-tabs">
            <div className={styles.tabs}>
                {tabs}
            </div>
            {activeChild}
        </div>
    );
};

export const getValidationStatus = ({isValid, expiredCount, expiringSoonCount, expirationDate, daysTilExpiration, documentIssues}) => {
    if(!lodash.isEmpty(documentIssues)) {
        return {
            style: styles.ValidationExpired,
            message: 'DOCUMENT ISSUE',
            label: lodash.map(documentIssues, issue => issue.label),
            labelStyle: styles.ValidationLabelStyle
        };
    }
    if (expiringSoonCount > 0 && expiredCount > 0) {
        return {
            style: styles.ValidationExpired,
            message: 'MULTIPLE ISSUES',
        };
    }
    if(expiringSoonCount > 0) {
        return {
            style: styles.ValidationExpiringSoon,
            message: 'EXPIRING SOON',
            label: daysTilExpiration ? `Expires in ${daysTilExpiration} days`: ''
        };
    }
    if(expiredCount > 0) {
        return {
            style: styles.ValidationExpired,
            message: 'EXPIRED',
            label: expirationDate ? <span>Expired on <DateTime value={expirationDate} format={'MMM DD, YYYY'} /></span> : ''
        };
    }
    if(isValid) {
        return {
            style: styles.ValidationPass,
            message: 'FILED',
            label: daysTilExpiration ? `Expires in ${daysTilExpiration} days`: ''
        };
    }
    
    return {
        style: styles.ValidationFail,
        message: 'MISSING'
    };
};

export default connect((state, props) => {
    const { router, location, params } = props;
    const binderId = params.id;
    const isLoading = binderValidationLoadingSelector(state);
    const documentValidations = binderValidationSelector(state);
    const institutionId = binderInstitutionIdSelector(state);
    const binderTemplateId = lodash.get(state, 'binder.binderTemplateId');
    const studyDocument = lodash.get(documentValidations, 'studyDocument');
    const staffDocument = lodash.get(documentValidations, 'staffDocument');
    const allDocumentValidations = lodash.get(documentValidations, 'allDocumentValidations');
    const staffMemberChanges = lodash.get(documentValidations, 'staffMemberChanges', {});
    const totalPercentComplete = lodash.get(documentValidations, 'totalPercentComplete');
    const uploads = lodash.get(documentValidations, 'uploads', {});

    const allowedMimeTypes = lodash.get(state, 'app.allowedMimeTypes', []);
    const fileExtensionLookup = lodash.get(state, 'app.fileExtensionLookup', {});
    const lastUploadedDocument = lodash.get(state, 'files.lastUploadedDocument', {});
    const store = {};
    

    return {
        binderId,
        institutionId,
        binderTemplateId,
        router,
        location,
        isLoading,
        studyDocument,
        staffDocument,
        allDocumentValidations,
        staffMemberChanges,
        totalPercentComplete,
        store,
        uploads,
        allowedMimeTypes,
        fileExtensionLookup,
        lastUploadedDocument
    };
}, {
    retrieveBinderValidation,
    unloadBinderValidation,
    writeUploadedDocument,
    setValidationRequirement,
    syncBinderValidationStaffMembers,
    checkBinderValidationStaffMembers,
    importBinderValidationTemplate,
    loadMimeTypes,
    push
})(BinderValidation);